import { queryOptions, useQuery } from '@tanstack/react-query';
import { fetchClient, type Usuario } from '../api';
import { queryClient } from '../queryClient';
import { SESSAO_QUERY_KEY } from './sessao.chave';

export * from './sessao.chave';

/**
 * Quem está logado é a API que sabe: os cookies de sessão são `HttpOnly`, então o
 * navegador não consegue olhar para eles. Por isso o front não guarda "estou logado"
 * em lugar nenhum — a sessão é uma query, e o cache dela é a fonte da verdade.
 *
 * `null` significa "não há sessão" e é cacheado como qualquer outra resposta, para a
 * pergunta não se repetir a cada rota.
 */
export const sessaoQueryOptions = queryOptions({
  queryKey: SESSAO_QUERY_KEY,
  queryFn: async (): Promise<Usuario | null> => {
    const { data } = await fetchClient.GET('/auth');

    return data ?? null;
  },
  staleTime: 60_000,
  retry: false,
});

/**
 * Para usar em `beforeLoad`: devolve a sessão do cache ou pergunta para a API.
 */
export function buscarSessao() {
  return queryClient.ensureQueryData(sessaoQueryOptions);
}

/**
 * Leitura síncrona do cache, para o código que não pode esperar. Devolve `undefined`
 * quando ninguém perguntou ainda — nas rotas autenticadas o guard já perguntou.
 */
export function lerSessao() {
  return queryClient.getQueryData(sessaoQueryOptions.queryKey);
}

export function useSessao() {
  return useQuery(sessaoQueryOptions);
}

/**
 * Registra no cache que não existe sessão, para quem já sabe disso sem precisar
 * perguntar. É o caso da tela de login exibindo um erro que a própria API acabou de
 * devolver: sem isso o cache fica frio e o cabeçalho pergunta de novo.
 */
export function definirSemSessao() {
  queryClient.setQueryData(sessaoQueryOptions.queryKey, null);
}

/**
 * Atalho para os guards de rota: sem sessão, ninguém tem permissão nenhuma.
 */
export async function buscarPermissoes() {
  const usuario = await buscarSessao();

  return usuario?.permissoes ?? [];
}

/**
 * Versão síncrona de `buscarPermissoes`, para quando o guard já perguntou.
 */
export function lerPermissoes() {
  return lerSessao()?.permissoes ?? [];
}
